import { GOOGLE_API_SCRIPT_URL } from '@/utils/firebase/googleApiDiagnostics/googleApi'

// Firewalls that drop rather than refuse the request leave the probe hanging, so cap it well below the 30-60s
// Firebase waits before giving up on the Google API iframe.
const PROBE_TIMEOUT_MS = 10_000

export type GoogleApiNetworkStatus =
  | 'reachable' // the request reached Google and came back
  | 'blocked' // the request never got a valid response back from Google
  | 'urlNotFound' // Google answered, but no longer serves this URL, so our constant has gone stale

// Checks whether the network path to the Google API domain is open. Detects blocking by VPN, proxy, firewall or
// device network configuration only - a browser extension can scope its rules to script requests and let this
// probe through, so a reachable result does not guarantee the script itself will load. See
// `isGoogleApiScriptLoadable` for that.
//
// A HEAD request is enough to prove the path is open without downloading the script.
export const checkGoogleApiNetwork = async (): Promise<GoogleApiNetworkStatus> => {
  try {
    const response = await fetch(GOOGLE_API_SCRIPT_URL, {
      method: 'HEAD',
      cache: 'no-store',
      signal: AbortSignal.timeout(PROBE_TIMEOUT_MS),
    })

    // Google no longer serving this URL says nothing about the customer's network, so report it separately rather
    // than blaming them for our stale constant. See `GOOGLE_API_SCRIPT_URL`.
    if (response.status === 404 || response.status === 410) {
      console.error(`${GOOGLE_API_SCRIPT_URL} responded with ${response.status}, the URL we probe may be out of date`)

      return 'urlNotFound'
    }

    // Any other status still proves the path is open. `apis.google.com` responds with `access-control-allow-origin:
    // *`, and a proxy serving its own block page in Google's place will not, which fails the CORS check and lands
    // in the catch below - so a response we can read at all is one that genuinely came back from Google.
    if (!response.ok) {
      console.error(`${GOOGLE_API_SCRIPT_URL} responded with ${response.status}`)
    }

    return 'reachable'
  } catch (e) {
    // The request was refused, timed out, blocked, or came back without Google's CORS headers.
    console.error(`Failed to reach ${GOOGLE_API_SCRIPT_URL}, federated login is likely blocked by the network`, e)

    return 'blocked'
  }
}
